/**
 * Quando un promemoria deve avvisare, e quali sono ancora da venire.
 *
 * ## Il difetto da cui nasce
 *
 * Fino al 03-10-2026 i promemoria erano **segnalibri**: si mettevano, si toglievano, si vedevano
 * nella guida — e all'ora del programma non succedeva niente. Dodici file li toccavano e nessuno
 * guardava l'orologio. Una funzione che si usa, risponde al tocco, e non fa la cosa che promette.
 *
 * ## Perché la regola sta qui
 *
 * Perché le due domande — «questo è il momento di avvisare?» e «quali restano?» — si sbagliano in
 * silenzio, e qui si provano senza montare niente e senza aspettare che passi un minuto vero.
 */

import { parseReminderKey, reminderKey } from './reminders.js';
import type { Program } from './guide.js';

/**
 * Quanto prima si avvisa.
 *
 * Un minuto: il tempo di alzare gli occhi e premere un tasto. Avvisare allo scoccare dell'ora
 * significherebbe arrivare sempre a programma iniziato; molto prima significherebbe un avviso che
 * si dimentica — e che copre quel che si sta guardando senza che serva ancora a niente.
 */
export const REMINDER_LEAD_MS = 60_000;

/**
 * Per quanto tempo l'avviso resta utile.
 *
 * Dopo l'inizio ha ancora senso per qualche minuto — chi torna in salotto fa ancora in tempo — ma
 * non per sempre: un avviso per un programma cominciato mezz'ora fa manda su un canale dove non c'è
 * più quel che prometteva.
 */
export const REMINDER_GRACE_MS = 5 * 60_000;

export interface ReminderEntry {
  readonly key: string;
  readonly channelId: string;
  readonly startMs: number;
  /**
   * Il programma, **quando sta nella finestra di palinsesto caricata**. Altrimenti `null`.
   *
   * Non è un caso raro: il palinsesto si chiede un canale per volta, e un promemoria messo dall'hero
   * su un evento di giovedì non ha nessun programma in memoria. Chi disegna deve saper comporre la
   * riga anche così — canale e ora li ha — e il titolo è la sola cosa che manca.
   */
  readonly program: Program | null;
}

/**
 * Fin quando un promemoria resta nell'elenco.
 *
 * Col programma in mano è la sua **fine**. Senza, non se ne conosce la durata, e si tiene fino alla
 * fine della finestra d'avviso: a quel punto il suo lavoro è fatto — l'avviso è scattato, o il
 * momento è passato — e lasciarlo lì vorrebbe dire un elenco che non si svuota mai.
 */
function until(entry: ReminderEntry): number {
  return entry.program === null ? entry.startMs + REMINDER_GRACE_MS : entry.program.endMs;
}

/** I programmi caricati, indicizzati con la **stessa** chiave dei promemoria. */
function byKey(programs: ReadonlyArray<Program>): Map<string, Program> {
  const mappa = new Map<string, Program>();
  for (const program of programs) mappa.set(reminderKey(program.channelId, program.startMs), program);
  return mappa;
}

/**
 * I promemoria ancora da venire, dal più vicino al più lontano.
 *
 * ## Si parte dalle chiavi, non dai programmi
 *
 * Era il contrario, ed è il difetto che si vedeva sul televisore: l'elenco si componeva scorrendo i
 * programmi **caricati**, quindi un promemoria il cui programma non era in memoria non compariva —
 * cioè tutti quelli messi dalla home, che sono eventi di giorni dopo. La fonte della verità è
 * l'elenco delle chiavi; i programmi servono solo a mettere un titolo dove c'è.
 *
 * ## «Da venire» vuol dire non ancora finito
 *
 * Non «non ancora cominciato»: un programma in onda adesso è esattamente quello che chi guarda
 * l'elenco vuole trovare in cima — sparirebbe dalla pagina proprio nel momento in cui serve.
 */
export function upcomingReminders(
  reminders: ReadonlySet<string>,
  programs: ReadonlyArray<Program>,
  nowMs: number,
): ReadonlyArray<ReminderEntry> {
  const noti = byKey(programs);
  const voci: ReminderEntry[] = [];

  for (const key of reminders) {
    const bersaglio = parseReminderKey(key);
    // Una chiave del formato di prima non si sa dove punti: non si mostra, e non si cancella.
    if (bersaglio === null) continue;

    const voce: ReminderEntry = {
      key,
      channelId: bersaglio.channelId,
      startMs: bersaglio.startMs,
      program: noti.get(key) ?? null,
    };
    if (until(voce) > nowMs) voci.push(voce);
  }

  return voci.sort((a, b) => a.startMs - b.startMs);
}

/**
 * Il promemoria da annunciare **adesso**, o `null`.
 *
 * Si restituisce **uno solo**, il più imminente: due avvisi insieme sono due riquadri sovrapposti su
 * cui non si può nemmeno premere, e la scelta su quale guardare la fa comunque chi guarda.
 *
 * `alreadyShown` sono quelli già annunciati in questa sessione. Senza, l'avviso ricomparirebbe a
 * ogni controllo per tutto il minuto — e un riquadro che si riapre da solo mentre lo si chiude è
 * indistinguibile da un'app impazzita.
 *
 * ## Si avvisa anche senza il programma
 *
 * L'ora d'inizio sta nella chiave, ed è tutto quel che serve per sapere che è il momento. Pretendere
 * il programma caricato avrebbe voluto dire non avvisare mai per i promemoria messi dalla home — gli
 * unici per cui l'avviso serve davvero, visto che sono di giorni dopo e uno se li dimentica. Il
 * titolo manca, e chi disegna dice quel che sa: il canale.
 */
export function reminderToAnnounce(
  reminders: ReadonlySet<string>,
  programs: ReadonlyArray<Program>,
  nowMs: number,
  alreadyShown: ReadonlySet<string>,
): ReminderEntry | null {
  const dovuti = upcomingReminders(reminders, programs, nowMs).filter((voce) => {
    if (alreadyShown.has(voce.key)) return false;
    const da = voce.startMs - REMINDER_LEAD_MS;
    const a = voce.startMs + REMINDER_GRACE_MS;
    return nowMs >= da && nowMs <= a;
  });

  return dovuti[0] ?? null;
}

/**
 * Quelli da dimenticare: finiti, e quindi inutili da tenere.
 *
 * Senza questa potatura l'elenco cresce per sempre e il controllo periodico scorre promemoria di
 * mesi fa a ogni giro. Si toglie **a programma finito** e non a programma annunciato: fra
 * l'annuncio e la fine c'è tutta la durata, ed è il momento in cui l'elenco serve di più.
 */
export function expiredReminders(
  reminders: ReadonlySet<string>,
  programs: ReadonlyArray<Program>,
  nowMs: number,
): ReadonlyArray<string> {
  /*
    I vivi li dice `upcomingReminders`, e non un secondo conto fatto qui.

    Erano due definizioni di «ancora da venire» in due funzioni dello stesso file, e divergevano
    proprio sul caso che conta: questa chiedeva che il programma fosse **caricato** per considerare
    scaduto un promemoria, perché senza la sua fine non sapeva quando scadesse. Ora la scadenza si
    ricava dalla chiave anche senza il programma, e il doppione non serve più.
  */
  const vivi = new Set(upcomingReminders(reminders, programs, nowMs).map((voce) => voce.key));

  /*
    Le chiavi illeggibili **non si toccano**.

    Sono quelle del formato di prima (`canale-slot`): non si sa a che ora puntino, quindi non si può
    dire che siano passate. Cancellare un dato che non si sa leggere è peggio che tenerlo: occupa
    una riga di `localStorage` e non fa danno.
  */
  return [...reminders].filter(
    (chiave) => parseReminderKey(chiave) !== null && !vivi.has(chiave),
  );
}
